/**
 * Drawings register search and order (W44), ArcGIS-free so karma can run it.
 * - A station typed in the search box is a station search (R43 `at_station`), not text: "1018+50", "1018+50.5",
 *   "STA 1018+50", or feet with the prefix ("sta 101850"). A bare number stays a text search: sheet numbers can be
 *   numbers ("4").
 * - Sheet numbers sort naturally: C-2 before C-10.
 */
import { stationName } from '../../../../../../../shared/helpers/alignment-stationing';

export type SheetSearch = { station: number; label: string } | { q: string } | null;

const STATION_NAME = /^(?:sta\.?\s*)?(\d+)\+(\d+(?:\.\d+)?)$/i;
const STATION_FEET = /^sta\.?\s*(\d+(?:\.\d+)?)$/i;

export function parseSheetSearch(text: string | null | undefined): SheetSearch {
  const value = String(text ?? '').trim();
  if (!value) {
    return null;
  }
  const name = STATION_NAME.exec(value);
  const feet = name ? Number(name[1]) * 100 + Number(name[2]) : STATION_FEET.exec(value) ? Number(STATION_FEET.exec(value)[1]) : NaN;
  if (Number.isFinite(feet) && feet >= 0 && feet <= 1e8) {
    return { station: Math.round(feet * 100) / 100, label: `STA ${stationName(feet)}` };
  }
  return { q: value };
}

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/** Natural order: "C-2" < "C-10" < "C-100"; case and accents ignored */
export function naturalCompare(a: string | null | undefined, b: string | null | undefined): number {
  return collator.compare(String(a ?? ''), String(b ?? ''));
}

export interface DisciplineGroup<T> {
  /** The discipline as written on the sheets ('' for none) */
  key: string;
  label: string;
  rows: T[];
}

/**
 * Sheets grouped by discipline (case-insensitive, the first spelling seen names the group), groups in natural order with
 * "No discipline" last, sheets in natural order of their number within each group.
 */
export function groupByDiscipline<T extends { discipline?: string | null; number?: string | null }>(rows: T[]): DisciplineGroup<T>[] {
  const groups = new Map<string, DisciplineGroup<T>>();
  for (const row of rows) {
    const name = (row.discipline ?? '').trim();
    const id = name.toLowerCase();
    if (!groups.has(id)) {
      groups.set(id, { key: name, label: name || 'No discipline', rows: [] });
    }
    groups.get(id).rows.push(row);
  }
  const list = [...groups.values()];
  list.forEach(g => g.rows.sort((a, b) => naturalCompare(a.number, b.number)));
  return list.sort((a, b) => (!a.key ? 1 : !b.key ? -1 : naturalCompare(a.key, b.key)));
}
